import { useRouter, useLocalSearchParams, useFocusEffect } from "expo-router";
import { useCallback, useState } from "react";
import { View, StyleSheet } from "react-native";
import { Text, Button } from "react-native-paper";

import type { QuestionComponentProps } from "./types";

import { fetchMedications } from "@/lib/api";
import { colors } from "@/lib/theme";

export function MedicationQuestion({ readOnly }: QuestionComponentProps) {
    const router = useRouter();
    const { id: visitId } = useLocalSearchParams<{ id: string }>();
    const [medicationCount, setMedicationCount] = useState(0);

    // Refetch medication count every time this screen comes into focus
    // (e.g. returning from the medications management screen)
    useFocusEffect(
        useCallback(() => {
            if (!visitId) {
                return;
            }
            fetchMedications(visitId)
                .then((meds) => setMedicationCount(meds.length))
                .catch(() => setMedicationCount(0));
        }, [visitId])
    );

    return (
        <View style={styles.card}>
            <Text style={styles.countText}>
                {medicationCount === 0
                    ? "No medications recorded yet"
                    : `${medicationCount} medication${medicationCount === 1 ? "" : "s"} recorded`}
            </Text>
            <Button
                mode="outlined"
                icon="pill"
                onPress={() => router.push(`/visit/${visitId}/medications`)}
                disabled={readOnly}
                style={styles.button}
                labelStyle={styles.buttonLabel}
            >
                Manage Medications
            </Button>
        </View>
    );
}

const styles = StyleSheet.create({
    row: {
        flexDirection: "row",
        gap: 16
    },
    option: {
        flexDirection: "row",
        alignItems: "center"
    },
    card: {
        marginTop: 12,
        padding: 16,
        backgroundColor: colors.backgroundSecondary,
        borderRadius: 8,
        borderWidth: 1,
        borderColor: colors.surfaceBorder
    },
    countText: {
        fontSize: 14,
        color: colors.textSecondary,
        marginBottom: 12
    },
    button: {
        borderColor: colors.primary
    },
    buttonLabel: {
        color: colors.primary
    }
});
